<template>
  <BaseSection
    heading="Gerät demontieren"
    data-testid="unMountDevice_expansion"
  >
    <BaseTextarea
      v-model:input-text="unmountedDeviceEvent.reason"
      label="Grund für Demontage"
      test-id="unmount_reason"
    />
    <div class="md:flex md:gap-4">
      <BaseFormInput
        v-model="unmountedDevice.serialNumber"
        class="md:basis-1/2"
        :disabled="true"
        label="Seriennummer"
        :optional="false"
        test-id="unmountedDeviceSerialnumber"
        :uppercase="isSRTDevice"
      />
      <BaseFormInput
        v-model="unmountedDevice.productKey"
        class="md:basis-1/2"
        :disabled="true"
        label="Artikelnummer"
        test-id="unmountedDeviceProductkey"
      />
    </div>
    <div v-if="isMeteringDevice" class="md:flex md:gap-4">
      <BaseFormInput
        v-model="unmountedDevice.yearOfConformity"
        class="pr-2 md:basis-1/2"
        label="Eichjahr"
        :disabled="true"
        type="number"
        inputmode="number"
        pattern="[0-9]{4}"
        placeholder="yyyy"
        test-id="unmountedDeviceYearOfConformity"
      />
    </div>
    <div class="md:flex">
      <DateMount
        v-if="isEdit"
        v-model:device-event="unmountedDeviceEvent"
        :disabled="true"
        test-id="unmounting-date"
        label="Demontagedatum"
        class="md:basis-1/2 md:pr-2"
      />
    </div>
    <div v-if="isMeasuringDevice" class="md:flex md:gap-4">
      <div class="md:basis-1/2">
        <BaseFormInput
          v-model.number="unmountedDeviceEvent.counter"
          label="Zählerstand bei Ausbau"
          inputmode="numeric"
          pattern="[0-9]*"
          placeholder="0,000"
          test-id="unmountedDeviceCounterOnUnmount"
          type="number"
        />
      </div>
      <div class="md:basis-1/2">
        <MeasurementUnit
          v-model:unit="unmountedDevice.measurementUnit"
          :device="unmountedDevice"
          test-id="unmountedDeviceMeasurementUnit"
        />
      </div>
    </div>
    <div v-if="isHCA" class="md:flex md:gap-4">
      <div class="md:basis-1/2">
        <BaseFormInput
          v-model.number="unmountedDeviceEvent.controlValue"
          :maxlength="2"
          label="Kontrollwert für Verdunster"
          inputmode="numeric"
          pattern="[0-9]{2}"
          placeholder="0"
          test-id="unmountedDeviceControlValueOnUnmount"
          type="number"
        />
      </div>
      <div class="md:basis-1/2">
        <div class="p-3">
          <div class="relative">
            <label
              class="absolute left-2.5 top-4 z-10 origin-[0] -translate-y-4 scale-75 text-sm text-gray-500 dark:text-gray-400"
            >
              Maßeinheit
            </label>
            <div
              class="px-2.5 pb-2.5 pt-5 text-sm text-gray-900 dark:text-white"
            >
              mm
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseSection>
</template>
<script setup lang="ts">
import MeasurementUnit from '@/components/Forms/DeviceFormComponents/MeasurementUnit.vue';
import { computed, WritableComputedRef } from 'vue';
import { DeviceClasses } from '@/models/devices/Device';
import BaseFormInput from '@/components/UI/Input/BaseFormInput.vue';
import BaseTextarea from '@/components/UI/Note/BaseTextarea.vue';
import { DeviceEventParams } from '@/models/installationPoint/DeviceEvent';
import { deviceTypes } from '@/enums/device';
import BaseSection from '@/components/UI/Card/BaseSection.vue';
import DateMount from '@/components/Forms/DeviceFormComponents/DateMount.vue';
import { FormAction, useFormStore } from '@/store/form/formStore';

const props = defineProps<{
  unmountedDevice: DeviceClasses;
  unmountedDeviceEvent: DeviceEventParams;
}>();

const emit = defineEmits([
  'update:unmountedDevice',
  'update:unmountedDeviceEvent',
]);

const unmountedDevice: WritableComputedRef<DeviceClasses> = computed({
  get() {
    return props.unmountedDevice;
  },
  set(value) {
    emit('update:unmountedDevice', value);
  },
});

const unmountedDeviceEvent: WritableComputedRef<DeviceEventParams> = computed({
  get() {
    return props.unmountedDeviceEvent;
  },
  set(value) {
    emit('update:unmountedDeviceEvent', value);
  },
});

const isSRTDevice = computed(() => {
  return (
    unmountedDevice.value.deviceType == deviceTypes.SmartRadiatorThermostat
  );
});

const isMeasuringDevice = computed(() => {
  return unmountedDevice.value.isMeasuringDevice();
});

const isHCA = computed(() => {
  return unmountedDevice.value.deviceType === deviceTypes.HeatCostAllocator;
});

const isEdit = computed(() => {
  return useFormStore().formAction === FormAction.EDIT;
});

const isMeteringDevice = computed(() => {
  return (
    unmountedDevice.value.isMeteringDevice() &&
    unmountedDevice.value.deviceType !== deviceTypes.HeatCostAllocator
  );
});
</script>
<style scoped></style>
